import { TranslateService } from '@ngx-translate/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormlyFieldSelect } from '../../../lib/formly/fields/select.type';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ListValues } from '../../../trash/shared/interfaces/form-input-config';
import { ICreateVehicleRequest, ILicensePlateResponse, IUpdateVehicleRequest, IVehicleResponse } from '../dtos';
import { FormlyPlateLicense } from '../../../lib/formly/fields/formly-plate-license/formly-plate-license.component';
import { ICountryVehicleLicensePlateValidatorResponse } from '../../../application-location-management';

export class UpsertVehiclesModel implements ICreateVehicleRequest {
  id?: string;
  vehicleType: string;
  licensePlateData: ILicensePlateResponse;
  makeId?: string | null;
  modelId?: string | null;
  productionYear?: number | null;
  // accessStatus: string;
  countryPlateValidator?: ICountryVehicleLicensePlateValidatorResponse;
  countryId?: string;
  plateLicenseTypeId?: string;

  private constructor(request: ICreateVehicleRequest) {
    this.id = request.id;
    this.vehicleType = request.vehicleType;
    this.licensePlateData = request.licensePlateData;
    this.makeId = request.makeId || null;
    this.modelId = request.modelId || null;
    this.productionYear = request.productionYear;
    // this.accessStatus = request.accessStatus;
    this.countryId = request.licensePlateData.country.id;
    this.plateLicenseTypeId = request.licensePlateData.plateLicenseType.id;
  }

  static create(data: ICreateVehicleRequest): UpsertVehiclesModel {
    return new UpsertVehiclesModel(data);
  }

  static createFromItem(item: IVehicleResponse): UpsertVehiclesModel {
    return new UpsertVehiclesModel({
      id: item.id,
      vehicleType: item.vehicleType,
      licensePlateData: item.licensePlate,
      makeId: item.make?.id ?? null,
      modelId: item.model?.id ?? null,
      productionYear: item.productionYear
      // accessStatus: item.accessStatus
    });
  }

  static createDefault(): UpsertVehiclesModel {
    return UpsertVehiclesModel.create({
      vehicleType: '',
      licensePlateData: {
        plateLetters: '',
        plateDigits: '',
        country: {
          id: '',
          englishName: ''
        },
        plateLicenseType: {
          id: '',
          englishName: ''
        }
      },
      makeId: null,
      modelId: null,
      productionYear: null
      // accessStatus: ''
    });
  }

  update(request: IUpdateVehicleRequest) {
    this.makeId = request.makeId;
    this.modelId = request.modelId;
    this.productionYear = request.productionYear;
    // this.accessStatus = request.accessStatus;
  }

  onCountryChange(country: ListValues): void {
    // Trigger update in the FormlyPlateLicense component when country changes
    if (this.countryPlateValidator) {
      // The component will automatically detect the change through the updateFormControls method
    }
    if (this.vehicleType) {
      // this component will automatically detect the change through the updateFormControls method
    }
  }

  onMakeChange(make: ListValues): void {
    // Trigger update in the FormlyFieldSelect component when make changes
    if (this.makeId) {
      // The component will automatically detect the change through the updateFormControls method
    }
  }

  onVehicleTypeChange(vehicleType: ListValues): void {
    // Trigger update in the FormlyPlateLicense component when vehicle type changes
    if (vehicleType.value) {
      this.vehicleType = vehicleType.value;
      // The component will automatically detect the change through the validator update
    }
  }

  setCountryPlateValidator(validator: ICountryVehicleLicensePlateValidatorResponse): void {
    this.countryPlateValidator = validator;
    console.log('Country plate validator is:', this.countryPlateValidator);
  }

  getFormlyConfig(
    translate: TranslateService,
    modalData: DynamicDialogConfig,
    isVisible: boolean,
    onCountryChangeCallback?: (country: ListValues) => void,
    onMakeChangeCallback?: (make: ListValues) => void,
    onVehicleTypeChangeCallback?: (vehicleType: ListValues) => void,
    vehicleTypeSignal?: () => string
  ): FormlyFieldConfig[] {
    return [
      {
        fieldGroupClassName: 'row mb-3',
        fieldGroup: [
          {
            className: 'col-12 col-lg-6 mb-3',
            key: 'countryId',
            type: FormlyFieldSelect,
            props: {
              label: translate.instant('GENERAL.COUNTRY'),
              placeholder: translate.instant('VEHICLE_FLOW.SELECT_COUNTRY'),
              required: true,
              appendTo: 'body',
              options: [],
              disabled: isVisible,
              change: (field: any, event: any) =>
                onCountryChangeCallback ? onCountryChangeCallback(event) : this.onCountryChange(event)
            },
            validation: {
              messages: {
                required: translate.instant('VEHICLE_FLOW.COUNTRY_REQUIRED')
              }
            }
          },
          {
            className: 'col-12 col-lg-6 mb-3',
            key: 'vehicleType',
            type: FormlyFieldSelect,
            props: {
              label: translate.instant('VEHICLE_FLOW.VEHICLE_TYPE'),
              placeholder: translate.instant('VEHICLE_FLOW.SELECT_TYPE'),
              appendTo: 'body',
              required: true,
              disabled: isVisible || !this.countryId,
              options: [],
              change: (field: any, event: any) =>
                onVehicleTypeChangeCallback ? onVehicleTypeChangeCallback(event) : this.onVehicleTypeChange(event)
            },
            validation: {
              messages: {
                required: translate.instant('GENERAL.THIS_FIELD_IS_REQUIRED')
              }
            }
          },
          {
            className: 'col-12 col-lg-6 mb-3',
            key: 'makeId',
            type: FormlyFieldSelect,
            props: {
              label: translate.instant('CUSTOMER_MANAGEMENT.VEHICLE_MAKE'),
              placeholder: translate.instant('VEHICLE_FLOW.SELECT_MAKE'),
              appendTo: 'body',
              options: [],
              filter: true,
              change: (field: any, event: any) =>
                onMakeChangeCallback ? onMakeChangeCallback(event) : this.onMakeChange(event)
            }
          },
          {
            className: 'col-12 col-lg-6 mb-3',
            key: 'modelId',
            type: FormlyFieldSelect,
            props: {
              label: translate.instant('CUSTOMER_MANAGEMENT.VEHICLE_MODEL'),
              placeholder: translate.instant('VEHICLE_FLOW.SELECT_MODEL'),
              appendTo: 'body',
              disabled: !this.makeId,
              options: [],
              filter: true
            }
          },
          {
            className: 'col-12 col-lg-6 mb-3',
            key: 'productionYear',
            type: 'input',
            props: {
              label: translate.instant('VEHICLE_FLOW.PRODUCTION_YEAR'),
              placeholder: 'Ex: 2020',
              type: 'number'
            }
          },
          // {
          //   className: 'col-12 col-lg-6 mb-3',
          //   key: 'accessStatus',
          //   type: FormlyFieldSelect,
          //   props: {
          //     label: translate.instant('VEHICLE_FLOW.ACCESS_STATUS'),
          //     placeholder: translate.instant('VEHICLE_FLOW.ACCESS_STATUS'),
          //     appendTo: 'body',
          //     options: []
          //   }
          // },
          {
            className: 'col-12 col-lg-6 mb-3',
            key: 'plateLicenseTypeId',
            type: FormlyFieldSelect,
            props: {
              label: translate.instant('VEHICLE_FLOW.PLATE_LICENSE_TYPE'),
              placeholder: translate.instant('VEHICLE_FLOW.SELECT_PLATE_LICENSE_TYPE'),
              appendTo: 'body',
              required: true,
              options: [],
              disabled: isVisible
            },
            validation: {
              messages: {
                required: translate.instant('VEHICLE_FLOW.PLATE_LICENSE_TYPE_REQUIRED')
              }
            }
          },
          {
            className: 'col-12 mb-3',
            key: 'licensePlateData',
            type: FormlyPlateLicense,
            hideExpression: (model: any) => {
              return !model.countryId || !model.vehicleType;
            },
            props: {
              countryPlateValidator: () => this.countryPlateValidator,
              vehicleTypeSignal: vehicleTypeSignal,
              disabled: isVisible
            }
          }
        ]
      }
    ];
  }
}
